const REVIEW_THUMBNAIL_TIME_SECONDS = 0.001;
const REVIEW_THUMBNAIL_MAX_WIDTH = 720;
const REVIEW_THUMBNAIL_MAX_HEIGHT = 1280;
const REVIEW_THUMBNAIL_QUALITY = 0.82;

function waitForVideoEvent(video: HTMLVideoElement, eventName: string) {
	return new Promise<void>((resolve, reject) => {
		const cleanup = () => {
			window.clearTimeout(timeout);
			video.removeEventListener(eventName, onEvent);
			video.removeEventListener("error", onError);
		};
		const onEvent = () => {
			cleanup();
			resolve();
		};
		const onError = () => {
			cleanup();
			reject(new Error("Could not load video to generate thumbnail."));
		};
		const timeout = window.setTimeout(() => {
			cleanup();
			reject(new Error("Timed out while generating video thumbnail."));
		}, 10000);

		video.addEventListener(eventName, onEvent, { once: true });
		video.addEventListener("error", onError, { once: true });
	});
}

function thumbnailFileName(videoFileName: string) {
	const base = videoFileName.replace(/\.[^.]+$/, "").replace(/[^a-zA-Z0-9._-]/g, "_");
	return `${base || "review-video"}-thumbnail.jpg`;
}

function scaledDimensions(width: number, height: number) {
	const scale = Math.min(
		1,
		REVIEW_THUMBNAIL_MAX_WIDTH / width,
		REVIEW_THUMBNAIL_MAX_HEIGHT / height,
	);
	return {
		width: Math.max(1, Math.round(width * scale)),
		height: Math.max(1, Math.round(height * scale)),
	};
}

export async function generateReviewVideoThumbnailFile(videoFile: File): Promise<File> {
	const objectUrl = URL.createObjectURL(videoFile);
	const video = document.createElement("video");

	try {
		video.src = objectUrl;
		video.muted = true;
		video.playsInline = true;
		video.preload = "metadata";
		video.load();

		await waitForVideoEvent(video, "loadedmetadata");
		const targetTime = Math.min(
			REVIEW_THUMBNAIL_TIME_SECONDS,
			Math.max(0, (Number.isFinite(video.duration) ? video.duration : 0) - 0.05),
		);

		if (targetTime > 0) {
			video.currentTime = targetTime;
			await waitForVideoEvent(video, "seeked");
		} else if (video.readyState < HTMLMediaElement.HAVE_CURRENT_DATA) {
			await waitForVideoEvent(video, "loadeddata");
		}

		const sourceWidth = video.videoWidth;
		const sourceHeight = video.videoHeight;
		if (!sourceWidth || !sourceHeight) {
			throw new Error("Could not read video dimensions to generate thumbnail.");
		}

		const size = scaledDimensions(sourceWidth, sourceHeight);
		const canvas = document.createElement("canvas");
		canvas.width = size.width;
		canvas.height = size.height;

		const context = canvas.getContext("2d");
		if (!context) {
			throw new Error("Could not create thumbnail canvas.");
		}
		context.drawImage(video, 0, 0, size.width, size.height);

		const blob = await new Promise<Blob>((resolve, reject) => {
			canvas.toBlob(
				(result) => {
					if (result) resolve(result);
					else reject(new Error("Could not create thumbnail image."));
				},
				"image/jpeg",
				REVIEW_THUMBNAIL_QUALITY,
			);
		});

		return new File([blob], thumbnailFileName(videoFile.name), { type: "image/jpeg" });
	} finally {
		video.removeAttribute("src");
		video.load();
		URL.revokeObjectURL(objectUrl);
	}
}
